/* eslint-disable react/jsx-curly-newline */
import React, { useState, useEffect, useCallback } from "react";
import { useLazyQuery } from "@apollo/client";
import { UseFormReturn, Controller } from "react-hook-form";
import { useToast } from "@npm-telluria-tecnologia/telluria-ui";

// Pages
import { IFormConcretingRequestInputs } from "@pages/ConcretingRequest";

// GraphQL
import {
  contractPumpTypeAllWithPagination,
  TContractPumpTypeAllWithPaginationQueries
} from "@graphql/groups/contractPumpType/query.graphql";
import {
  ConcretingRequestType,
  ContractPumpTypeQueriesGetAllWithPaginationArgs,
  ContractPumpTypeType,
  ECommandResultStatus,
  EConcretingRequestPump,
  EWhereClausesOperators
} from "@store/graphql/helpers/types.graphql";

// Languages
import useTranslation from "@languages/useTranslation";
import { BaseCardMessages } from "src/languages/interfaces";

// Helpers
import { handleErrorMessage } from "@helpers/index";

// Libraries
import {
  TextField,
  FormControl,
  FormHelperText,
  CircularProgress,
  InputLabel,
  Select,
  MenuItem
} from "@libraries/mui";

import * as Styled from "./styles";

interface FormConcretingRequestPumpProps {
  controlForms: UseFormReturn<IFormConcretingRequestInputs>;
  currentConcretingRequest: ConcretingRequestType | undefined;
  onlyView: boolean;
}

interface CustomOptionType {
  id: string;
  value: string;
}

/**
 * Default pump options used as a fallback when the selected contract has no
 * pump types registered (or no contract has been selected yet). They feed the
 * legacy `pump` enum field, preserving back-compatibility.
 */
export const pumpOptions: CustomOptionType[] = [
  { id: EConcretingRequestPump.NO_PUMP, value: "Sem bomba" },
  { id: EConcretingRequestPump.STATIONARY, value: "Bomba estacionária" },
  { id: EConcretingRequestPump.BOOM, value: "Bomba lança" }
];

/**
 * Pump step of the concreting-request form.
 *
 * Renders the pump-type selector with options sourced dynamically from the
 * client's contract (via GraphQL, filtered by `contractId` and `status=ACTIVE`).
 * When the contract has pump types, the selection is bound to the
 * `contractPumpTypeId` form field. When the contract has none — or the query
 * fails — a visual notice is shown and the field falls back to the 3 default
 * options bound to the legacy `pump` enum field.
 */
const FormConcretingRequestPump: React.FC<FormConcretingRequestPumpProps> = ({
  controlForms,
  currentConcretingRequest,
  onlyView
}) => {
  const { t } = useTranslation();
  const { addToast } = useToast();

  const {
    control,
    setValue,
    formState: { errors: formErrors },
    watch
  } = controlForms;

  // watch values
  const watchContract = watch("contract");

  // dynamic pump-type options sourced from the contract
  const [pumpTypeOptions, setPumpTypeOptions] = useState<ContractPumpTypeType[]>(
    []
  );

  // query for the contract pump types
  const [
    getContractPumpTypes,
    {
      loading: loadingContractPumpTypes,
      error: errorContractPumpTypes,
      data: dataContractPumpTypes
    }
  ] = useLazyQuery<
    TContractPumpTypeAllWithPaginationQueries,
    ContractPumpTypeQueriesGetAllWithPaginationArgs
  >(
    contractPumpTypeAllWithPagination(["id", "code", "description", "status"]),
    {
      fetchPolicy: "network-only"
    }
  );

  const loadContractPumpTypes = useCallback(() => {
    const response = dataContractPumpTypes?.contractPumpType
      .getAllWithPagination;

    if (response?.status === ECommandResultStatus.SUCCESS) {
      setPumpTypeOptions((response.result as ContractPumpTypeType[]) ?? []);

      return;
    }

    setPumpTypeOptions([]);
  }, [dataContractPumpTypes]);

  useEffect(() => {
    if (watchContract) {
      getContractPumpTypes({
        variables: {
          where: [
            {
              property: "contractId",
              operator: EWhereClausesOperators.EQUALS,
              value: watchContract.id
            },
            {
              property: "status",
              operator: EWhereClausesOperators.EQUALS,
              value: "ACTIVE"
            }
          ]
        }
      });
    } else {
      setPumpTypeOptions([]);
    }
  }, [watchContract, getContractPumpTypes]);

  useEffect(() => {
    if (dataContractPumpTypes) loadContractPumpTypes();
    if (errorContractPumpTypes) {
      // On query failure we fall back to the default options to avoid blocking
      // the request, but still surface the error to the user.
      setPumpTypeOptions([]);
      addToast({
        type: "error",
        title: "Internal client error",
        description: errorContractPumpTypes.message
      });
    }
  }, [
    addToast,
    dataContractPumpTypes,
    errorContractPumpTypes,
    loadContractPumpTypes
  ]);

  useEffect(() => {
    if (!currentConcretingRequest) return;

    if (currentConcretingRequest.contractPumpType?.id) {
      setValue(
        "contractPumpTypeId",
        currentConcretingRequest.contractPumpType.id
      );
    } else {
      setValue("pump", currentConcretingRequest.pump);
    }

    setValue("observationPump", currentConcretingRequest.observationPump ?? "");
    setValue("pipingDistance", currentConcretingRequest.pipingDistance ?? "");
  }, [currentConcretingRequest, setValue]);

  // The dynamic select only mounts after its options load, so the pre-fill above can
  // run before the field exists. Re-apply the saved selection once the options are
  // available so the previously chosen pump type shows up when editing.
  useEffect(() => {
    if (currentConcretingRequest?.contractPumpType?.id && pumpTypeOptions.length > 0) {
      setValue(
        "contractPumpTypeId",
        currentConcretingRequest.contractPumpType.id
      );
    }
  }, [currentConcretingRequest, pumpTypeOptions, setValue]);

  const hasPumpTypes = pumpTypeOptions.length > 0;

  return (
    <Styled.FormContainer>
      {loadingContractPumpTypes && (
        <FormControl className="default-field" size="small" fullWidth>
          <CircularProgress color="inherit" size={20} />
        </FormControl>
      )}

      {!loadingContractPumpTypes && hasPumpTypes && (
        <Controller
          name="contractPumpTypeId"
          control={control}
          defaultValue=""
          render={({ field }) => (
            <Styled.CustomFormControl
              error={!!formErrors.contractPumpTypeId}
              className="default-field"
              size="small"
              fullWidth
            >
              <InputLabel id="contract-pump-type-select">
                {t(BaseCardMessages.concretingRequestPumpDescriptionText)}
              </InputLabel>
              <Select
                {...field}
                labelId="contract-pump-type-select"
                label={t(BaseCardMessages.concretingRequestPumpDescriptionText)}
                error={!!formErrors.contractPumpTypeId}
                onChange={(event) => setValue(
                  "contractPumpTypeId",
                  event.target.value as string
                )}
                disabled={onlyView}
              >
                {pumpTypeOptions.map((option) => (
                  <MenuItem key={option.id} value={option.id}>
                    <Styled.SelectItem>{option.description}</Styled.SelectItem>
                  </MenuItem>
                ))}
              </Select>
              {!!formErrors.contractPumpTypeId && (
                <FormHelperText>
                  {handleErrorMessage(formErrors.contractPumpTypeId, t)}
                </FormHelperText>
              )}
            </Styled.CustomFormControl>
          )}
        />
      )}

      {!loadingContractPumpTypes && !hasPumpTypes && (
        <>
          <Styled.FallbackNotice data-testid="pump-fallback-notice">
            {t(BaseCardMessages.concretingRequestNoPumpTypesAvailableText)}
          </Styled.FallbackNotice>
          <Controller
            name="pump"
            control={control}
            defaultValue={pumpOptions[0].id}
            render={({ field }) => (
              <Styled.CustomFormControl
                error={!!formErrors.pump}
                className="default-field"
                size="small"
                fullWidth
              >
                <InputLabel id="pump-select">
                  {t(BaseCardMessages.concretingRequestPumpDescriptionText)}
                </InputLabel>
                <Select
                  {...field}
                  labelId="pump-select"
                  label={t(
                    BaseCardMessages.concretingRequestPumpDescriptionText
                  )}
                  error={!!formErrors.pump}
                  disabled={onlyView}
                >
                  {pumpOptions.map((option) => (
                    <MenuItem key={option.id} value={option.id}>
                      <Styled.SelectItem>{option.value}</Styled.SelectItem>
                    </MenuItem>
                  ))}
                </Select>
                {!!formErrors.pump && (
                  <FormHelperText>
                    {handleErrorMessage(formErrors.pump, t)}
                  </FormHelperText>
                )}
              </Styled.CustomFormControl>
            )}
          />
        </>
      )}

      <Controller
        name="pipingDistance"
        control={control}
        defaultValue={0}
        render={({ field }) => (
          <TextField
            {...field}
            type="number"
            autoComplete="off"
            label={t(BaseCardMessages.concretingRequestPipingDistanceText)}
            variant="outlined"
            error={!!formErrors.pipingDistance}
            helperText={handleErrorMessage(formErrors.pipingDistance, t)}
            className="default-field"
            disabled={onlyView}
            size="small"
            InputProps={{
              inputProps: {
                min: 0
              }
            }}
          />
        )}
      />

      <Controller
        name="observationPump"
        control={control}
        defaultValue=""
        render={({ field }) => (
          <TextField
            {...field}
            type="text"
            autoComplete="off"
            label={t(BaseCardMessages.concretingRequestObservationText)}
            variant="outlined"
            error={!!formErrors.observationPump}
            helperText={handleErrorMessage(formErrors.observationPump, t)}
            className="default-field"
            disabled={onlyView}
            size="small"
            InputProps={{
              inputProps: {
                min: 0
              }
            }}
          />
        )}
      />
    </Styled.FormContainer>
  );
};

export default FormConcretingRequestPump;
